import { replaceHtmlToStr } from 'utils/utils';
import { ClaudeProposalHotelInfo, Content } from 'ts/shared/interfaces/ProposalData';

export interface HotelInfoConfig {
  language: string;
  details?: boolean;
  overview?: boolean;
  imagesIndexes: number[];
}

export const resolveHotelInfo = (information?: any): any => {
  if (!information) {
    return undefined;
  }

  if ('hotelInfo' in information && information.hotelInfo) {
    return information.hotelInfo;
  }

  if ('hotelDetails' in information && information.hotelDetails) {
    return information.hotelDetails;
  }

  if ('checkIn' in information || 'images' in information || 'hotelFacilities' in information) {
    return information;
  }

  return undefined;
};

export const getHotelInfoDescription = (hotelInfo?: any, language = 'en') => {
  if (!hotelInfo) {
    return '';
  }

  const hotelInfoLang =
    hotelInfo.hotelInfoLangs?.find((lang: any) => lang.languageCode === language) ||
    hotelInfo.hotelInfoLangs?.[0];

  const desc =
    hotelInfoLang?.hotelMainDescription || hotelInfo.description || hotelInfo.hotelDesc || '';

  return replaceHtmlToStr(desc);
};

export const getHotelInfoFacilityText = (facility: any) =>
  typeof facility === 'string' ? facility : facility.desc || facility.descLang || '';

export const getHotelInfoImageUrl = (image: any) => image.imageLink || '';

export const getFullHotelInfoForProposal = (information?: any): any => {
  const hotelInfo = resolveHotelInfo(information);

  if (!hotelInfo) {
    return undefined;
  }

  return {
    ...hotelInfo,
    images: [...(hotelInfo.images || [])].slice(0, 10),
    hotelFacilities: [...(hotelInfo.hotelFacilities || [])],
  };
};

export const getDefaultHotelInfoLanguage = (hotelInfo?: any, i18nLang = 'en') => {
  const languages = hotelInfo?.hotelInfoLangs?.map((lang: any) => lang.languageCode) || [];

  if (!languages.length) {
    return i18nLang;
  }

  if (languages.includes(i18nLang)) {
    return i18nLang;
  }

  if (i18nLang === 'he' && languages.includes('he')) {
    return 'he';
  }

  return languages[0];
};

export const createDefaultHotelInfoConfig = (
  hotelInfo?: any,
  i18nLang = 'en',
): HotelInfoConfig => ({
  language: getDefaultHotelInfoLanguage(hotelInfo, i18nLang),
  imagesIndexes: [],
});

export const getHotelInfoConfigKey = (hotel: any) =>
  hotel?.hotel?.code || hotel?.itemCode || hotel?.item?.itemCode;

export const parseHotelInfoConfigs = (externalParam_1?: string): any => {
  if (!externalParam_1) {
    return {};
  }

  try {
    const params = JSON.parse(externalParam_1);
    if (!params.hotelInfoConfig) {
      return {};
    }

    return typeof params.hotelInfoConfig === 'string'
      ? JSON.parse(params.hotelInfoConfig)
      : params.hotelInfoConfig;
  } catch {
    return {};
  }
};

export const resolveHotelInfoConfig = (configs: any, hotel: any, hotelIndex: number): any => {
  const itemCode = getHotelInfoConfigKey(hotel);

  if (itemCode && configs[itemCode]) {
    return configs[itemCode];
  }

  const configKeys = Object.keys(configs);
  if (configKeys.length === 1) {
    return configs[configKeys[0]];
  }

  if (hotelIndex < configKeys.length) {
    return configs[configKeys[hotelIndex]];
  }

  return Object.values(configs)[hotelIndex];
};

export const applyHotelInfoConfigToExternalParam = (
  externalParam_1: string | undefined,
  hotel: any,
  config?: any,
): string => {
  const itemCode = getHotelInfoConfigKey(hotel);
  if (!itemCode || !config) {
    return externalParam_1 || '{}';
  }

  let params: any = {};
  if (externalParam_1) {
    try {
      params = JSON.parse(externalParam_1);
    } catch {
      params = {};
    }
  }

  const configs = {
    ...parseHotelInfoConfigs(externalParam_1),
    [itemCode]: config,
  };

  return JSON.stringify({
    ...params,
    hotelInfoConfig: JSON.stringify(configs),
  });
};

const CLIENT_CLAUDE_LANG: Record<string, string[]> = {
  he: ['Hebrew', 'he'],
  en: ['English', 'en'],
  es: ['Spanish', 'es'],
  it: ['Italian', 'it'],
  fr: ['French', 'fr'],
};

export const getClientClaudeContent = (content: Content[] | undefined, i18nLang: string) => {
  if (!content?.length) {
    return undefined;
  }

  const candidates = CLIENT_CLAUDE_LANG[i18nLang.toLowerCase()] || [i18nLang];
  for (const candidate of candidates) {
    const found = content.find(
      (item) =>
        item.language === candidate || item.language.toLowerCase() === candidate.toLowerCase(),
    );
    if (found) {
      return found;
    }
  }

  return content[0];
};

export type HotelDescriptionSource = {
  aI_HotelInformatiom?: ClaudeProposalHotelInfo | null;
  tripAdvisorHotel?: { description?: string } | null;
  serpHotel?: { description?: string } | null;
  hotelInformatiom?: any;
};

export const getTripBuilderHotelDescription = (card: HotelDescriptionSource, i18nLang: string) => {
  const claudeContent = getClientClaudeContent(card.aI_HotelInformatiom?.content, i18nLang);
  const aiSummary = claudeContent?.summary?.trim();

  if (aiSummary) {
    return aiSummary;
  }

  const tripadvisorDescription = card.tripAdvisorHotel?.description?.trim();
  if (tripadvisorDescription) {
    return replaceHtmlToStr(tripadvisorDescription).trim();
  }

  const serpDescription = card.serpHotel?.description?.trim();
  if (serpDescription) {
    return replaceHtmlToStr(serpDescription).trim();
  }

  const hotelInfo = resolveHotelInfo(card.hotelInformatiom);
  const checkIn = hotelInfo?.checkIn?.trim();

  return checkIn ? replaceHtmlToStr(checkIn).trim() : '';
};
